/*******************************************************************************
 NAME     : [SideMenuMainTab]
 PURPOSE  : SideMenuMainTab
 REVISIONS:
 Ver.       Date        Author           Description
 ---------  ----------  ---------------  ------------------------------------
 1.0        2025-02-06  이형동           1. 최초 작성
 1.1        2025-05-14  유경진           2. 기능 수정 (탭간전환시 선택된 tab에 해당하는 group만 expand)
 *******************************************************************************/
import React from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { SIDEMENU_MODE } from 'e3-common/constants/enumerates';
import styles from './SideMenu.module.css';
import { useBoundStore } from 'e3-common/store/useBoundStore';
import { HOME_MENU_ID } from 'e3-common/constants/constMenuId';

import PropTypes from 'prop-types';
import _ from 'lodash';

const SideMenuMainTab = ({ sideMenuMode, onMenuClick, showAllMenuInSide = false }) => {
    const { hierarchyMenu, selectedTopMenu, selectedMenu } = useBoundStore((state) => state.menu);
    const hierRef = useRef([]);
    const expandStateRef = useRef({});
    const [menuItems, setMenuItems] = useState([]);
    const prevSelectedTopMenuRef = useRef(null);
    const prevSelectedMenuRef = useRef(null);

    /**
     * 메뉴 아이템의 확장 상태를 저장
     *
     * @param {Array} items - 메뉴 아이템 배열
     * @returns {void}
     */
    const saveExpandState = (items) => {
        if (!items) return;

        items.forEach(item => {
            if (item.children && item.children.length > 0) {
                expandStateRef.current[item.menuId] = item.expanded;
                saveExpandState(item.children);
            }
        });
    };

    /**
     * 저장된 확장/축소 상태를 메뉴 아이템에 적용
     *
     * @param {Array} items - 메뉴 아이템 배열
     * @returns {Array} 확장/축소 상태가 적용된 메뉴 아이템 배열
     */
    const applyExpandState = (items) => {
        if (!items) return items;

        return items.map(item => {
            const result = { ...item };

            if (item.children && item.children.length > 0 && expandStateRef.current[item.menuId] !== undefined) {
                result.expanded = expandStateRef.current[item.menuId];
            }

            if (item.children) {
                result.children = applyExpandState(item.children);
            }

            return result;
        });
    };

    /**
     * 활성화된 메뉴가 있는지 확인
     *
     * @returns {boolean} 활성화된 메뉴 존재 여부
     */
    const hasActiveMenu = useCallback(() => {
        return selectedMenu && selectedMenu.menuId && selectedMenu.menuId !== HOME_MENU_ID;
    }, [selectedMenu]);

    /**
     * 선택된 메뉴 경로 찾기
     */
    const findMenuPath = useCallback((items, targetId, path = []) => {
        if (!items || !targetId) return null;

        for (const item of items) {
            const currentPath = [...path, item];

            if (item.menuId === targetId) {
                return currentPath;
            }

            if (item.children && item.children.length > 0) {
                const foundPath = findMenuPath(item.children, targetId, currentPath);
                if (foundPath) return foundPath;
            }
        }

        return null;
    }, []);

    /**
     * 특정 메뉴까지의 경로에 있는 모든 부모 메뉴를 확장 상태로 설정
     */
    const expandMenuPath = useCallback((items, menuPath) => {
        if (!items || !menuPath || menuPath.length <= 1) return items;

        const pathIds = new Set(menuPath.map(item => item.menuId));

        const expandPath = (menuItems) => {
            if (!menuItems) return menuItems;

            return menuItems.map(item => {
                const result = { ...item };

                if (pathIds.has(item.menuId) && item.children && item.children.length > 0) {
                    result.expanded = true;
                    expandStateRef.current[item.menuId] = true;
                }

                if (item.children) {
                    result.children = expandPath(item.children);
                }

                return result;
            });
        };

        return expandPath(items);
    }, []);

    const settingExpandedRef = useRef((node) => {
        if (!node.children || node.children.length === 0) {
            return false;
        }

        for (let child of node.children) {
            if (child.menuId === selectedMenu.menuId || settingExpandedRef.current(child)) {
                node.expanded = true;
                return true;
            }
        }

        return false;
    });

    /**
     * 선택된 메뉴 ID가 변경될 때 settingExpandedRef 함수 업데이트
     */
    useEffect(() => {
        settingExpandedRef.current = (node) => {
            if (!node.children || node.children.length === 0) {
                return false;
            }

            for (let child of node.children) {
                if (child.menuId === selectedMenu.menuId || settingExpandedRef.current(child)) {
                    node.expanded = true;
                    expandStateRef.current[node.menuId] = true;
                    return true;
                }
            }

            return false;
        };
    }, [selectedMenu.menuId]);

    /**
     * 메뉴 확장/축소 토글 함수에 대한 ref
     *
     * @type {React.MutableRefObject<Function>}
     */
    const toggleExpandRef = useRef((menuId, items) => {
        return items.map((menuItem) => {
            if (menuItem.menuId === menuId) {
                const newExpandedState = !menuItem.expanded;
                expandStateRef.current[menuId] = newExpandedState;

                return {
                    ...menuItem,
                    expanded: newExpandedState,
                    children: menuItem.children
                        ? toggleExpandRef.current(menuId, menuItem.children)
                        : [],
                };
            }

            if (menuItem.children) {
                return {
                    ...menuItem,
                    children: toggleExpandRef.current(menuId, menuItem.children),
                };
            }

            return menuItem;
        });
    });

    /**
     * 모든 메뉴를 축소하는 함수
     */
    const collapseAllMenus = useCallback((items) => {
        if (!items) return items;

        return items.map(item => {
            const result = { ...item };

            if (item.children && item.children.length > 0) {
                result.expanded = false;
                expandStateRef.current[item.menuId] = false;
            }

            if (item.children) {
                result.children = collapseAllMenus(item.children);
            }

            return result;
        });
    }, []);

    /**
     * 선택된 메뉴 그룹을 자동으로 확장
     */
    const expandSelectedMenuGroup = useCallback((baseItems) => {
        if (!baseItems || !hasActiveMenu()) return baseItems;

        const topLevelItems = showAllMenuInSide ? hierarchyMenu : baseItems;
        const menuPath = findMenuPath(topLevelItems, selectedMenu.menuId);
        if (!menuPath) return baseItems;

        return expandMenuPath(baseItems, menuPath);
    }, [hasActiveMenu, selectedMenu, findMenuPath, expandMenuPath, showAllMenuInSide, hierarchyMenu]);

    /**
     * 선택된 메뉴가 변경되었을 때 메뉴 그룹 확장
     */
    useEffect(() => {
        if (prevSelectedMenuRef.current !== selectedMenu.menuId && hasActiveMenu()) {
            prevSelectedMenuRef.current = selectedMenu.menuId;

            setMenuItems(prevItems => {
                if (!prevItems || prevItems.length === 0) return prevItems;

                const updatedItems = _.cloneDeep(prevItems);
                return expandSelectedMenuGroup(updatedItems);
            });
        }
    }, [selectedMenu, hasActiveMenu, expandSelectedMenuGroup]);

    /**
     * 탭 전환 감지 및 처리
     */
    useEffect(() => {
        if (prevSelectedTopMenuRef.current !== selectedTopMenu?.menuId) {
            prevSelectedTopMenuRef.current = selectedTopMenu?.menuId;

            if (hasActiveMenu()) {
                prevSelectedMenuRef.current = null;
            } else {
                setMenuItems(prevItems => {
                    if (!prevItems || prevItems.length === 0) return prevItems;

                    const updatedItems = _.cloneDeep(prevItems);
                    return collapseAllMenus(updatedItems);
                });
            }
        }
    }, [selectedTopMenu, hasActiveMenu, collapseAllMenus, expandSelectedMenuGroup]);

    /**
     * 메뉴 데이터가 변경될 때 메뉴 아이템 업데이트 및 확장 상태 적용
     */
    useEffect(() => {
        saveExpandState(menuItems);

        if (showAllMenuInSide) {
            if (!hierRef.current?.length) {
                hierRef.current = _.cloneDeep(hierarchyMenu);
            }

            if (selectedMenu?.menuId === HOME_MENU_ID) {
                hierRef.current = applyExpandState(_.cloneDeep(hierarchyMenu));
            } else {
                const freshHierarchy = _.cloneDeep(hierarchyMenu);
                if (hasActiveMenu()) {
                    const menuPath = findMenuPath(freshHierarchy, selectedMenu.menuId);
                    if (menuPath) {
                        const expandedHierarchy = expandMenuPath(freshHierarchy, menuPath);
                        hierRef.current = applyExpandState(expandedHierarchy);
                    } else {
                        freshHierarchy.forEach((node) => settingExpandedRef.current(node));
                        hierRef.current = applyExpandState(freshHierarchy);
                    }
                } else {
                    hierRef.current = applyExpandState(freshHierarchy);
                }
            }

            setMenuItems(hierRef.current);
        } else {
            const children = hierarchyMenu?.find(
                (f) => f.menuId === selectedTopMenu?.menuId
            )?.children;

            if (!children) {
                setMenuItems([]);
                return;
            }

            let expandedList = children?.map((m) => ({
                ...m,
                ...(m.children?.length > 0 && {
                    expanded: hasActiveMenu()
                        ? expandStateRef.current[m.menuId] !== undefined
                            ? expandStateRef.current[m.menuId]
                            : false
                        : false
                }),
            }));

            expandedList = applyExpandState(expandedList);

            if (hasActiveMenu()) {
                const menuPath = findMenuPath(expandedList, selectedMenu.menuId);
                if (menuPath) {
                    expandedList = expandMenuPath(expandedList, menuPath);
                }
            }

            setMenuItems(expandedList);
        }
    }, [hierarchyMenu, selectedTopMenu, showAllMenuInSide, selectedMenu, hasActiveMenu, findMenuPath, expandMenuPath]);

    const menuStyle = (e, lvl) => {
        let className = [];

        if (showAllMenuInSide) {
            className.push(styles.fromTopMenu);
        }
        if (lvl === 1) {
            className.push(styles.m01);
        } else if (lvl === 2) {
            className.push(styles.m02);
        } else if (lvl === 3) {
            className.push(styles.m03);
        } else if (lvl === 4) {
            className.push(styles.m04);
        } else if (lvl === 5) {
            className.push(styles.m05);
        }

        if (e.children?.length > 0) {
            if (!e.expanded) {
                className.push(styles.collapsed);
            } else {
                className.push(styles.expanded);
            }
        } else if (lvl === 1) {
            className.push(styles.nosubmenu);
        }
        if (selectedMenu.menuId === e.menuId) {
            className.push(styles.selected);
        }
        return className.join(' ');
    };

    const toggleExand = (menuId) => {
        setMenuItems((prev) => {
            const result = toggleExpandRef.current(menuId, prev);
            return result;
        });
    };

    /**
     * 메뉴 리스트를 생성
     *
     * @param {Array} childrenMenuList - 메뉴 항목 배열
     * @param {number} lvl - 현재 메뉴 레벨(깊이)
     * @returns {JSX.Element[]} 메뉴 항목 JSX 요소 배열
     */
    const createMenuList = (childrenMenuList, lvl) => {
        if (!childrenMenuList || childrenMenuList.length === 0) {
            return;
        }

        return childrenMenuList
            ?.filter((f) => f.sideYn === 'Y' && f.displayYn === 'Y')
            .map((e) => {
                return (
                    <li
                        key={e.menuId}
                        className={menuStyle(e, lvl)}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (e.children?.length > 0) {
                                toggleExand(e.menuId);
                            } else {
                                onMenuClick(e);
                            }
                        }}
                    >
                        <span>{e.menuName}</span>
                        {e.expanded && e.children?.length > 0 && (
                            <>
                                <ul key={e.menuId}>{createMenuList(e.children, lvl + 1)}</ul>
                            </>
                        )}
                    </li>
                );
            });
    };

    return (
        <div
            className={styles.inner}
            style={{
                display: sideMenuMode === SIDEMENU_MODE.MENU ? 'block' : 'none',
            }}
        >
            {showAllMenuInSide ? null : <h2>{selectedTopMenu?.menuName}</h2>}
            {menuItems?.length > 0 ? (
                <ul>{createMenuList(menuItems, showAllMenuInSide ? 1 : 2)}</ul>
            ) : null}
        </div>
    );
};

SideMenuMainTab.propTypes = {
    sideMenuMode: PropTypes.string,
    onMenuClick: PropTypes.func,
    showAllMenuInSide: PropTypes.bool,
};

SideMenuMainTab.displayName = 'SideMenuMainTab';
export default SideMenuMainTab;